// Case study content powering /case-studies/:slug
// Layout is unified — only data differs per project.

export const caseStudies = {
  "nara-therapy": {
    slug: "nara-therapy",
    title: "Nara Therapy",
    tagline: "Mental wellness, made part of every day.",
    role: "Product Designer",
    year: "2024",
    timeline: "6 months",
    team: "PM, 2 Engineers, 1 Designer",
    platform: "iOS · Android · Web",
    accent: "emerald",
    heroImage:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/oaqndel1_Blog%20Default%20Nav%20Bar%20State.jpg",
    overview:
      "Nara Therapy is a mental wellness platform that combines therapy and self-care for individuals, while giving licensed therapists tools like dashboards, scheduling, and mood tracking. At Nara, I worked on shaping the product vision and experience — designing intuitive features, simplifying onboarding, and ensuring the platform delivers value to both patients and professionals.",
    problem:
      "After the first MVP, retention was the biggest hurdle. Users would sign up, complete one or two check-ins, and quietly drop off. Therapists, on the other side, needed cleaner tools to manage appointments and follow up between sessions.",
    process: {
      heading: "The Process",
      body:
        "I conducted user interviews with therapists and ran secondary research on platforms like Headspace, Balance, and Calm to understand how they achieved stickiness — one of the core problems users faced after the first MVP. Insights from this research led to a redesign of the dashboard/homepage and the addition of features that increased user interaction and engagement by 43% in 30 days.",
      steps: [
        {
          label: "Discover",
          text:
            "10 interviews with therapists and members. Audit of Headspace, Balance, and Calm to map proven retention loops.",
        },
        {
          label: "Define",
          text:
            "Reframed the problem from 'add more content' to 'give people a reason to open the app today'.",
        },
        {
          label: "Design",
          text:
            "Rebuilt the home dashboard around a daily ritual: check-in → daily task → streak → reflect.",
        },
        {
          label: "Ship & measure",
          text:
            "Released in phases with PostHog instrumentation. +43% engagement in 30 days vs the prior MVP.",
        },
      ],
    },
    features: [
      {
        title: "Daily tasks based on user behavior",
        body:
          "Personalized micro-actions — mood check-ins, gratitude prompts, share-love messages — surfaced based on how the member has been engaging.",
      },
      {
        title: "Streaks and streak rewards",
        body:
          "Lightweight gamification that celebrates consistency without shaming missed days. Recovers gracefully when life gets in the way.",
      },
      {
        title: "Mood Charts",
        body:
          "Members and their therapists can see emotional patterns over time, turning vague feelings into a shared, actionable signal.",
      },
      {
        title: "Nara AI",
        body:
          "Private, on-demand chat that helps members process thoughts and regulate emotions between therapy sessions.",
      },
    ],
    screens: [
      {
        src: "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/vqkm5qyp_Blue.png",
        caption: "Daily tasks home with weekly streak rail.",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/8ol2se5a_Group%201000005114.png",
        caption: "Mood check-in flow and history.",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/8kdi75aa_Blue.png",
        caption: "Journal prompt with media attachments.",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/u9fcsl43_iPhone-13-Pro-Front%20%281%29.png",
        caption: "Guided breathing session.",
      },
      {
        src: "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/5w4k56k4_Blue.png",
        caption: "Nara AI — private chat for emotional regulation.",
      },
    ],
    outcomes: [
      { metric: "+43%", label: "Engagement in 30 days" },
      { metric: "92%", label: "Re-book a 2nd therapy session" },
      { metric: "87%", label: "Members feel calmer & in control" },
      { metric: "74%", label: "More likely to stay with employer" },
    ],
    liveUrl: "https://joinnara.com",
    video:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/8qmmhv3x_Screen%20Recording%202026-05-26%20at%202.31.10%E2%80%AFPM.mov",
  },

  "prompt-earn": {
    slug: "prompt-earn",
    title: "Prompt Earn",
    tagline: "Get paid promoting products you actually love.",
    comingSoon: true,
    role: "Product Designer",
    year: "2023",
    timeline: "4 months",
    team: "PM, 3 Engineers, 1 Designer",
    platform: "Web · iOS · Android",
    accent: "lime",
    heroImage:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/478h9yxf_Frame%20811636.png",
    overview:
      "Prompt Earn lets anyone promote valuable digital and physical products and get paid in dollars from anywhere in the world. I shaped the end-to-end promoter experience — from a 5-minute onboarding to a withdrawal flow that felt instant.",
    problem:
      "Affiliate platforms were either too technical for first-time promoters or too leaky on the payout side. New users dropped at three points: signup, first share, and first withdrawal.",
    process: {
      heading: "The Process",
      body:
        "I mapped the existing funnel with the data team, ran 8 user tests with target promoters across three markets, and benchmarked five global affiliate platforms. The redesign collapsed onboarding into three screens and brought the first reward forward.",
      steps: [
        { label: "Audit", text: "Funnel analysis exposed 62% drop between signup and first share." },
        { label: "Interview", text: "8 promoters across Nigeria, Kenya, and the Philippines." },
        { label: "Redesign", text: "3-screen signup, in-app share kits, instant-feel withdrawals." },
        { label: "Iterate", text: "Weekly releases driven by funnel metrics." },
      ],
    },
    features: [
      { title: "5-minute onboarding", body: "Three screens from landing to first share kit ready." },
      { title: "Share kits", body: "Pre-built copy, images, and links for each product — no creative work required." },
      { title: "Dollar wallet", body: "USD balance with low-friction withdrawal to local currency." },
      { title: "Performance dashboard", body: "Clear leaderboard, conversion %, and earnings visualization." },
    ],
    screens: [],
    outcomes: [
      { metric: "135K+", label: "Promoters onboarded" },
      { metric: "5 min", label: "Median time to first share" },
      { metric: "+38%", label: "Activation lift after redesign" },
    ],
    liveUrl: null,
  },

  "qaya-technologies": {
    slug: "qaya-technologies",
    title: "Qaya Technologies",
    tagline:
      "Optimize your supermarket checkout experience with our suite of solutions.",
    comingSoon: true,
    role: "Product Manager",
    year: "2023",
    timeline: "8 months",
    team: "Cross-functional · 12 people",
    platform: "Web · iOS · Android",
    accent: "yellow",
    heroImage:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/3qjp6bp3_Frame%202147225854%20%282%29.png",
    overview:
      "Qaya delivers digital solutions for commercial and agency banking, powered by AI and seamless integrations. I led product strategy across a multi-product suite — from agent onboarding to transaction reconciliation.",
    problem:
      "Field agents lost hours each day reconciling transactions across disconnected systems. Branch managers had no real-time view of activity.",
    process: {
      heading: "The Process",
      body:
        "Embedded with agents for a week, shadowed three branches, mapped 47 distinct workflows, and prioritized six for the v1. Built a north-star agent journey and aligned eng around weekly outcome reviews.",
      steps: [
        { label: "Field research", text: "1 week shadowing agents in 3 branches." },
        { label: "Workflow mapping", text: "47 workflows mapped, 6 prioritized for v1." },
        { label: "Roadmap", text: "Quarterly outcome-based plan with shared metrics." },
        { label: "Ship", text: "Phased rollout to 200+ agents with weekly retros." },
      ],
    },
    features: [
      { title: "Smart reconciliation", body: "AI-assisted matching cuts daily close-out time by 80%." },
      { title: "Agent passport", body: "One identity that works across all banking partners." },
      { title: "Live branch view", body: "Managers see activity, queues, and risk in real time." },
      { title: "Built-in compliance", body: "KYC and AML checks baked into every flow." },
    ],
    screens: [],
    outcomes: [
      { metric: "−80%", label: "Daily reconciliation time" },
      { metric: "200+", label: "Agents onboarded in 90 days" },
      { metric: "99.4%", label: "Transaction success rate" },
    ],
    liveUrl: null,
  },

  "fontalk": {
    slug: "fontalk",
    title: "Fontalk",
    tagline:
      "Phone service that helps you build credit through daily actions. Your trust grows into real opportunities.",
    role: "Product Designer",
    year: "2025",
    timeline: "4 months",
    team: "8 Software Engineers, 2 QAs, 1 DevOps, 1 Designer",
    platform: "iOS · Android",
    accent: "blue",
    heroImage:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/ryvgyqh5_How%20It%20Works.jpg",
    overview:
      "Fontalk turns the most ordinary thing in your pocket — your phone bill — into something that opens doors. Paying for talk, text, and data on time quietly builds a credit profile that unlocks better rates, housing applications, and financial products. I designed the end-to-end experience: a guided onboarding that gathers what's needed without feeling like a credit check, a frictionless SIM-installation flow that works across iOS and Android, and a home that shows the user how every action moves their trust score forward.",
    problem:
      "Millions of people — newcomers, students, freelancers, the underbanked — have no credit footprint because the systems that grade them don't see the bills they pay every month. Existing 'rent reporting' tools are slow, manual, and only show up after months. Phone bills, paid every single cycle, are an untapped on-ramp.",
    process: {
      heading: "The Process",
      body:
        "Fontalk lives or dies on trust. I ran 14 user interviews across two markets — half with people who had thin credit files, half who'd been turned down for a credit product in the past year. The pattern was clear: people would happily share data IF they understood exactly what was being collected, why, and what they got in return. The whole experience is built around that contract.",
      steps: [
        {
          label: "Discover",
          text:
            "14 interviews. Mapped the emotional friction of credit checks vs. the calm of paying a phone bill.",
        },
        {
          label: "Define",
          text:
            "Reframed the product from 'wireless plan' to 'wireless plan that earns you a credit footprint'.",
        },
        {
          label: "Design",
          text:
            "Five-screen onboarding, transparent plan builder, dual SIM flow for iOS and Android.",
        },
        {
          label: "Ship & learn",
          text:
            "Phased beta across 3 carriers. 91% activation completion on first session, 4.7/5 user trust score.",
        },
      ],
    },
    features: [
      {
        title: "Plan, paid, points",
        body:
          "Pick a plan, pay on time, and watch a real credit profile build month after month — automatically reported to bureaus.",
      },
      {
        title: "Transparent collection",
        body:
          "Every piece of data we ask for is justified inline. Users can see exactly what is shared, with whom, and why.",
      },
      {
        title: "Dual-OS SIM activation",
        body:
          "Tailored eSIM and physical SIM guides for iOS and Android — including a recovery flow when activation fails the first time.",
      },
      {
        title: "Daily action loop",
        body:
          "A home that rewards on-time payments, plan upgrades, and verified address updates with tangible trust-score movement.",
      },
    ],
    screens: [],
    journey: [
      {
        step: "01",
        label: "Marketing site",
        title: "A web home that earns the install",
        body:
          "Most users meet Fontalk on the web first. The marketing site is built to answer the two questions a new visitor asks — 'what is this?' and 'is it safe?' — through a Home page, a clear How-it-Works walkthrough, an About page, an honest FAQ, a Support hub, and a Community page that signals real users.",
        bullets: [
          "Six purposeful pages — no marketing fluff",
          "How-it-Works explains the credit-building loop in 3 steps",
          "Community + Support pages address trust head-on",
        ],
        images: [
          "/fontalk/web/Home.jpg",
          "/fontalk/web/How%20it%20Works.jpg",
          "/fontalk/web/About.jpg",
          "/fontalk/web/FAQs.jpg",
          "/fontalk/web/Support.jpg",
          "/fontalk/web/Community.jpg",
        ],
      },
      {
        step: "02",
        label: "Onboarding",
        title: "A four-screen welcome that sets the contract upfront",
        body:
          "Before users hand over any information, Fontalk explains the deal in plain language: a phone plan that quietly builds your credit. Each onboarding screen surfaces a single promise — connect, build, thrive, unlock — so the value lands before the work starts.",
        bullets: [
          "One promise per screen — never a wall of marketing copy",
          "Visual progress so the user always knows how close they are to done",
          "Skip-friendly: users can dive in and complete the rest in-app",
        ],
        images: [
          "/fontalk/onboarding1/Onboarding%201.jpg",
          "/fontalk/onboarding1/Onboarding%202.jpg",
          "/fontalk/onboarding1/Onboarding%203.jpg",
          "/fontalk/onboarding1/Onboarding%204.jpg",
        ],
      },
      {
        step: "03",
        label: "Account & Plan — eSIM",
        title: "Create an account and build the plan you actually need",
        body:
          "An eight-step focused flow takes the user from account creation through plan customization, address collection, payment, and a confident success state. Each step is its own modal — never an intimidating multi-page form.",
        bullets: [
          "Build-your-own plan with live totals",
          "Address & payment collected only when needed, with clear justification",
          "Success screens preview what comes next, not just celebrate",
        ],
        images: [
          "/fontalk/onboarding2/Set%20Up%20Your%20Account.jpg",
          "/fontalk/onboarding2/Set%20Up%20Your%20Account-1.jpg",
          "/fontalk/onboarding2/Build%20your%20plan.jpg",
          "/fontalk/onboarding2/Address%20Collection.jpg",
          "/fontalk/onboarding2/Payment%20Options.jpg",
          "/fontalk/onboarding2/Order%20Summary.jpg",
          "/fontalk/onboarding2/Success.jpg",
          "/fontalk/onboarding2/Success-1.jpg",
        ],
      },
      {
        step: "04",
        label: "Physical SIM alternative",
        title: "A second path for devices that need a physical card",
        body:
          "Not every device supports eSIM, and not every user wants it. The physical-SIM flow mirrors the eSIM setup but adds the shipping path — collect shipping address, confirm billing, review the order, and a success screen that sets expectations on delivery.",
        bullets: [
          "Account setup, plan build, and address collection in one focused flow",
          "Separate billing and shipping address screens — fewer mistakes",
          "Order summary + success state that previews delivery timing",
        ],
        images: [
          "/fontalk/phys_sim/Set%20Up%20Your%20Account.jpg",
          "/fontalk/phys_sim/Set%20Up%20Your%20Account-1.jpg",
          "/fontalk/phys_sim/Set%20Up%20Your%20Account-2.jpg",
          "/fontalk/phys_sim/Set%20Up%20Your%20Account-3.jpg",
          "/fontalk/phys_sim/Build%20your%20plan.jpg",
          "/fontalk/phys_sim/Build%20your%20plan-1.jpg",
          "/fontalk/phys_sim/Shipping%20Address.jpg",
          "/fontalk/phys_sim/Billing%20Address.jpg",
          "/fontalk/phys_sim/Billing.jpg",
          "/fontalk/phys_sim/Order%20Summary.jpg",
          "/fontalk/phys_sim/Success.jpg",
          "/fontalk/phys_sim/Success-1.jpg",
        ],
      },
      {
        step: "05",
        label: "Number selection",
        title: "Pick a fresh Fontalk number",
        body:
          "Users who want a clean start choose a new number from a focused picker, then confirm via a summary screen that shows everything about their new line before they commit.",
        bullets: [
          "Filter by area code and pattern preferences",
          "Compatibility summary surfaces eSIM-ready devices automatically",
          "One-tap reservation while the user finishes setup",
        ],
        images: [
          "/fontalk/number/Number%20Selection.jpg",
          "/fontalk/number/Number%20Selection-1.jpg",
          "/fontalk/number/Summary%2C%20%28If%20phone%20is%20eSIM%20copatible.jpg",
        ],
      },
      {
        step: "06",
        label: "Number transfer (port-in)",
        title: "Bring your number — designed for the messy real world",
        body:
          "Porting in is where most carriers lose users. Fontalk's transfer flow handles every state explicitly: in-progress, success, ineligible numbers, and resolution-required cases — each with a clear next step.",
        bullets: [
          "Eligibility check up-front so users know what to expect",
          "Distinct success paths for eSIM and physical SIM transfers",
          "Friendly recovery for ineligible numbers and stuck transfers",
        ],
        images: [
          "/fontalk/number/Number%20Transfer.jpg",
          "/fontalk/number/Number%20Transfer-1.jpg",
          "/fontalk/number/Number%20Transfer-2.jpg",
          "/fontalk/nt/SIM%20Porting.jpg",
          "/fontalk/nt/Transfer_%20eSIM%20Success.jpg",
          "/fontalk/nt/Transfer_%20Physical%20SIM%20In%20Progress.jpg",
          "/fontalk/nt/Transfer_%20Physical%20SIM%20Success.jpg",
          "/fontalk/nt/Transfer%20Status_%20Resolution%20Required.jpg",
          "/fontalk/nt/Number%20Transfer_%20Inelligibe.jpg",
          "/fontalk/nt/Number%20Transfer_%20Inelligibe%20Transfer.jpg",
          "/fontalk/nt/New%20Number%20Success%20Screen.jpg",
          "/fontalk/number/Number%20Transfer_%20Error.jpg",
        ],
      },
      {
        step: "07",
        label: "Pay with cash",
        title: "Cash-friendly checkout for the underbanked",
        body:
          "Fontalk's core user often doesn't have a credit card to pay with. The Pay-with-Cash flow lets a user pick a nearby store, generate a barcode, and pay in person — keeping the door open for the exact users who benefit most from credit-building.",
        bullets: [
          "Step-by-step how-it-works explainer before the barcode is generated",
          "Nearby-store picker with multiple options",
          "Barcode + success state confirms the payment without confusion",
        ],
        images: [
          "/fontalk/cash/Payment%20Options.jpg",
          "/fontalk/cash/Pay%20With%20Cash.jpg",
          "/fontalk/cash/How%20it%20Works_%20Step%201.jpg",
          "/fontalk/cash/How%20it%20Works_%20Step%202.jpg",
          "/fontalk/cash/How%20it%20Works_%20Step%203.jpg",
          "/fontalk/cash/Choose%20Your%20Store.jpg",
          "/fontalk/cash/Choose%20Your%20Store-1.jpg",
          "/fontalk/cash/Choose%20Your%20Store-2.jpg",
          "/fontalk/cash/Barcode.jpg",
          "/fontalk/cash/Ready%20to%20Build.jpg",
          "/fontalk/cash/Success.jpg",
        ],
      },
      {
        step: "08",
        label: "SIM activation — iOS",
        title: "Get talking in minutes on Apple",
        body:
          "iOS users activate instantly via eSIM, or follow a step-by-step manual flow if their device or carrier requires it. The guide adapts based on what the device reports back.",
        bullets: [
          "Instant eSIM provisioning where supported",
          "Two-track manual activation guide with progress checkmarks",
          "Inline help when a step looks unfamiliar",
        ],
        images: [
          "/fontalk/sim_ios/SIM%20Installation%20Guide_Instant%20Activation.jpg",
          "/fontalk/sim_ios/SIM%20Installation%20Guide_Manual%20Activation.jpg",
          "/fontalk/sim_ios/SIM%20Installation%20Guide_Manual%20Activation-1.jpg",
        ],
      },
      {
        step: "09",
        label: "SIM activation — Android",
        title: "Same ease, fully native on Android",
        body:
          "Android gets its own activation experience — including a recovery flow for the moment an eSIM hand-off fails, with a one-tap path back to manual activation.",
        bullets: [
          "Instant + manual paths with platform-native components",
          "First-class error recovery so a failure isn't a dead end",
          "Designed against the messiest Android OEM realities",
        ],
        images: [
          "/fontalk/sim_android/SIM%20Installation%20Guide_Instant%20Activation.jpg",
          "/fontalk/sim_android/SIM%20Installation%20Guide_Manual%20Activation.jpg",
          "/fontalk/sim_android/eSIM%20error.jpg",
        ],
      },
      {
        step: "10",
        label: "Login",
        title: "One identity, two platforms",
        body:
          "Login is intentionally minimal. Same credentials work on iOS and Android, with platform-native input styling so the screen feels at home on every device.",
        bullets: [
          "Single email + password — no third-party walls",
          "Platform-native input affordances on iOS and Android",
          "Forgot-password is one tap from the login screen",
        ],
        images: [
          "/fontalk/login/Login-IOS.jpg",
          "/fontalk/login/Login-Android.jpg",
        ],
      },
      {
        step: "11",
        label: "Usage",
        title: "See exactly how your trust is growing",
        body:
          "A calm, scannable usage view shows data, talk, and text in one place. Filters let users zoom in by period; the empty state is friendly and explains what they'll see once data flows in.",
        bullets: [
          "One screen for data, talk, and text",
          "Filterable by day, week, month, or billing cycle",
          "Friendly empty state — never a blank wall",
        ],
        images: [
          "/fontalk/usage/Usage.jpg",
          "/fontalk/usage/Usage-1.jpg",
          "/fontalk/usage/Filter.jpg",
          "/fontalk/usage/Usage%20Empty%20State.jpg",
          "/fontalk/usage/Usage%20Empty%20State-1.jpg",
        ],
      },
      {
        step: "12",
        label: "Billing & Payments",
        title: "Every payment is a step toward credit",
        body:
          "Billing is the engine of trust. Users see a clear list of bills, full payment history, and tap any transaction for a detailed receipt — every on-time payment is reinforced as a credit-building action.",
        bullets: [
          "Bill list with status, due date, and amount at a glance",
          "Payment history with filters by status and date",
          "Tap-to-view receipts for every transaction",
        ],
        images: [
          "/fontalk/billing/Billing.png",
          "/fontalk/billing/Billing%20Empty%20State.png",
          "/fontalk/billing/Payment%20History.png",
          "/fontalk/billing/Payment%20History-1.png",
          "/fontalk/billing/Filter.png",
          "/fontalk/billing/Transactions%20Receipt.png",
        ],
      },
      {
        step: "13",
        label: "Settings",
        title: "Calm, complete control over the account",
        body:
          "Settings collects everything a user might ever want to change — profile, devices, security, notifications, payment methods, language, legal, and a careful two-step account deletion. Every change is one tap from a clean hub.",
        bullets: [
          "Profile editing with avatar change & remove flows",
          "Security: password, device management, two-step delete account",
          "Notification, renewal reminder, and usage-alert preferences",
          "Payment methods, language, and legal in dedicated screens",
        ],
        images: [
          "/fontalk/settings/Settings.jpg",
          "/fontalk/settings/Settings-1.jpg",
          "/fontalk/settings/My%20Profile.jpg",
          "/fontalk/settings/Edit%20Profile.jpg",
          "/fontalk/settings/Change%20Image.jpg",
          "/fontalk/settings/Remove%20photo%20prompt.jpg",
          "/fontalk/settings/Security%20%20Settings.jpg",
          "/fontalk/settings/Change%20Password.jpg",
          "/fontalk/settings/Device%20Management.jpg",
          "/fontalk/settings/Delete%20Account.jpg",
          "/fontalk/settings/Delete%20Account-1.jpg",
          "/fontalk/settings/Delete%20Account-2.jpg",
          "/fontalk/settings/Notifications%20Settings.jpg",
          "/fontalk/settings/Renewal%20Reminders.jpg",
          "/fontalk/settings/Usage%20Alerts.jpg",
          "/fontalk/settings/Payment%20Methods.jpg",
          "/fontalk/settings/Language.jpg",
          "/fontalk/settings/Legal%20Stuff.jpg",
          "/fontalk/settings/Summary.jpg",
        ],
      },
    ],
    outcomes: [
      { metric: "91%", label: "Activation completion on first session" },
      { metric: "4.7/5", label: "User-reported trust after onboarding" },
      { metric: "<8 min", label: "Median time from signup to live line" },
      { metric: "+38%", label: "Plan-upgrade rate after first month" },
    ],
    liveUrl: null,
  },
  "ai-job-finder": {
    slug: "ai-job-finder",
    title: "AI Job Finder",    tagline:
      "Let AI find, apply, and secure your next role — so you can focus on what you do best.",
    role: "Product Designer",
    year: "2024",
    timeline: "5 months",
    team: "PM, 4 Engineers, 1 Designer",
    platform: "Web · iOS · Android",
    accent: "blue",
    heroImage:
      "https://customer-assets.emergentagent.com/job_site-starter-kit-6/artifacts/65nkhvtk_Hero.png",
    overview:
      "AI Job Finder is an end-to-end job-hunt agent. It learns what kind of role each user wants, finds matches across hundreds of boards, drafts and submits tailored applications, then keeps the user posted on every reply. I designed the full experience — from a question-light onboarding to an analytics view that proves the agent is actually working.",
    problem:
      "Job seekers spend 11+ hours per week on the same loop: scroll boards, rewrite the same résumé, fill out near-identical forms, and refresh inboxes. Most great-fit roles are missed simply because nobody is looking at 2am on a Tuesday. The few tools that automate this are either spammy (apply to everything) or shallow (job alerts only).",
    process: {
      heading: "The Process",
      body:
        "I shadowed 12 active job seekers for two weeks, watched 40+ application sessions, and benchmarked LinkedIn Easy Apply, Indeed, Otta, and Sonara. The insight that shaped the product: people don't want more jobs — they want fewer, better ones with the boring work already done.",
      steps: [
        {
          label: "Research",
          text:
            "Interviewed 12 job seekers, 6 recruiters. Mapped the 9-step status quo into 3 jobs-to-be-done.",
        },
        {
          label: "Define",
          text:
            "Reframed from 'help me find jobs' to 'do the application loop while I sleep'.",
        },
        {
          label: "Design",
          text:
            "5-screen onboarding, AI-curated home feed, automated submissions, transparent application tracker.",
        },
        {
          label: "Validate",
          text:
            "Closed beta with 80 users. 4.6/5 satisfaction, 3.2× faster to first interview vs prior tools.",
        },
      ],
    },
    features: [
      {
        title: "AI-Powered Job Matching",
        body:
          "Resume + preferences + behavior become a living profile. The agent surfaces roles ranked by fit score — not by recency or sponsorship.",
      },
      {
        title: "Automated Application Submissions",
        body:
          "Approve in one tap. The agent fills out the form, customizes the cover letter, and submits — all under your review controls.",
      },
      {
        title: "Continuous Monitoring & Alerts",
        body:
          "Listens 24/7 across boards and ATS systems. Pings you the moment a recruiter views the application or schedules an interview.",
      },
      {
        title: "Performance Analytics & Reporting",
        body:
          "See response rates, time-to-reply, and which résumé variants convert best. Tune the agent based on real data.",
      },
    ],
    screens: [],
    journey: [
      {
        step: "01",
        label: "Sign up & Verify",
        title: "A 60-second front door",
        body:
          "Anyone can be one step from applying. Email + password, a single verification screen, and they're in. No social-network walls, no résumé requirement to get past the homepage.",
        bullets: [
          "Email + password, no third-party walls",
          "One-screen email verification with resend",
          "Helpful inline validation on every input",
        ],
        images: [
          "/applyverse/Sign-up.jpg",
          "/applyverse/Create%20Password.jpg",
          "/applyverse/Email%20Verification.jpg",
        ],
      },
      {
        step: "02",
        label: "Tell us about you",
        title: "Goal-first questionnaire — not a form wall",
        body:
          "Instead of one giant form, we ask short, conversational questions: How would you like to share your story? What kind of role are you after? Pay range? When do you want to start? Each question is its own screen so the user is never overwhelmed.",
        bullets: [
          "Resume upload OR LinkedIn import OR build from scratch",
          "Job preference, target roles, salary, and start window — one at a time",
          "Optional 'additional questions' for stronger AI matching",
        ],
        images: [
          "/applyverse/How%20will%20you%20like%20to%20tell%20us%20about%20yourself.jpg",
          "/applyverse/What%20is%20your%20job%20preference_.jpg",
          "/applyverse/What%20is%20your%20job%20preference_-1.jpg",
          "/applyverse/What%20roles%20would%20you%20like%20to%20apply%20for_.jpg",
          "/applyverse/What%20roles%20would%20you%20like%20to%20apply%20for_-1.jpg",
          "/applyverse/What%20roles%20would%20you%20like%20to%20apply%20for_-2.jpg",
          "/applyverse/What%20is%20your%20salary%20expectation_.jpg",
          "/applyverse/When%20do%20you%20want%20to%20start.jpg",
          "/applyverse/When%20do%20you%20want%20to%20start-1.jpg",
          "/applyverse/Additional%20Questions.jpg",
        ],
      },
      {
        step: "03",
        label: "Set up your profile",
        title: "Work history, education and skills — in clean modals",
        body:
          "A side-rail checklist tracks progress through Personal info → Work Experience → Education → Skills. Each opens as a focused modal so users never lose their place. Progress auto-saves after every entry.",
        bullets: [
          "Add multiple work experiences, edit or remove anytime",
          "Education with degree, course, grade, CGPA",
          "Searchable skills picker with one-tap add",
        ],
        images: [
          "/applyverse/Setup%20Profile_Work%20Experience.jpg",
          "/applyverse/Setup%20Profile_Work%20Experience-1.jpg",
          "/applyverse/Setup%20Profile_Work%20Experience-2.jpg",
          "/applyverse/Setup%20Profile_Education.jpg",
          "/applyverse/Setup%20Profile_Skills.jpg",
          "/applyverse/Setup%20Profile_Skills-1.jpg",
        ],
      },
      {
        step: "04",
        label: "Profile complete",
        title: "Ready to apply in one click",
        body:
          "The success screen does double duty — it confirms the user is done and immediately surfaces the next valuable action: start matching. No dead-end celebration screens.",
        bullets: [
          "Confirms the agent is now active",
          "One CTA: start matching",
          "Mobile-friendly handoff to home feed",
        ],
        images: [
          "/applyverse/Success.jpg",
          "/applyverse/Success-1.jpg",
        ],
      },
      {
        step: "05",
        label: "Login & Recovery",
        title: "Frictionless re-entry",
        body:
          "Repeat sessions skip the noise. Clean email/password login, a three-step forgot-password flow that takes the user from request → code → new password → confirmation, with clear progress on every screen.",
        bullets: [
          "Single-field email + password login",
          "3-step forgot-password: request → code → reset",
          "Confirmation screen so users never wonder if it worked",
        ],
        images: [
          "/applyverse/login/Login.jpg",
          "/applyverse/login/Forgot%20Password.jpg",
          "/applyverse/login/Forgot%20Password-1.jpg",
          "/applyverse/login/Forgot%20Password-2.jpg",
          "/applyverse/login/Success.jpg",
        ],
      },
      {
        step: "06",
        label: "Home & matches",
        title: "An AI-curated feed of fits, not jobs",
        body:
          "The home feed ranks every match with a fit score derived from skills, comp band, location preference, and behavioural signals. Tap a card for full job details, or let the agent fill the application out on your behalf.",
        bullets: [
          "Fit score reasoning on every card",
          "Full job detail view with company + role context",
          "Agent auto-fills applications — review then send",
        ],
        images: [
          "/applyverse/home/Home.jpg",
          "/applyverse/home/Job%20Details.jpg",
          "/applyverse/home/Home_%20Filling%20out%20application.jpg",
        ],
      },
      {
        step: "07",
        label: "Your Applications",
        title: "Every submission, tracked end-to-end",
        body:
          "Three clear stages — In Progress, Ready to Send, and Submitted — give users a calm, scannable view of every application. Drill into any job for full detail, status, and the exact résumé/cover variant that was sent.",
        bullets: [
          "Three-stage pipeline: In Progress · Ready to Send · Submitted",
          "Per-application detail with résumé snapshot",
          "One-click review and send for AI-prepared applications",
        ],
        images: [
          "/applyverse/applications/Your%20Application.jpg",
          "/applyverse/applications/Your%20Applications_In%20Progress.jpg",
          "/applyverse/applications/Your%20Applications_Ready%20to%20Send.jpg",
          "/applyverse/applications/Your%20Applications_Submitted%20Applications.jpg",
          "/applyverse/applications/Job%20Details.jpg",
          "/applyverse/applications/Job%20Details_%20Ready%20to%20send.jpg",
        ],
      },
      {
        step: "08",
        label: "Settings",
        title: "One place for billing, security, and account control",
        body:
          "Settings consolidates everything users need to manage their account in one calm view — billing plans, payment history, password changes, and account deletion — each in its own focused screen with predictable layout.",
        bullets: [
          "Billing: view plan, upgrade, and see payment history",
          "Security: change password with strength feedback",
          "Account: clear, two-step delete flow with safeguards",
        ],
        images: [
          "/applyverse/settings/Settings_Billing.jpg",
          "/applyverse/settings/Settings_Billing-1.jpg",
          "/applyverse/settings/Settings_Billing-2.jpg",
          "/applyverse/settings/Settings_Change%20Password.jpg",
          "/applyverse/settings/Settings_Delete%20Account.jpg",
        ],
      },
    ],
    outcomes: [
      { metric: "11 → 2", label: "Hours per week spent job hunting" },
      { metric: "3.2×", label: "Faster to first interview vs status quo" },
      { metric: "4.6/5", label: "Beta user satisfaction (n=80)" },
      { metric: "+62%", label: "Reply rate on AI-tailored applications" },
    ],
    liveUrl: null,
  },
};

export const caseStudyOrder = [
  "nara-therapy",
  "fontalk",
  "ai-job-finder",
  "prompt-earn",
  "qaya-technologies",
];
